import React from "react";
import "../embed/styles/base.css";
import { useStore } from "../context/StoreContext";
import { createChatClient } from "../lib/chatApi";
import type { OnAddToCartResponse, RecommendedProduct } from "../lib/types";
import { RecommendationsWidget } from "../embed/pages/RecommendationsWidget";
import {
  appendTestSessionId,
  persistTestConfig,
  persistTestSessionId,
  updateGlobalTestConfig,
} from "../embed/config";
import { EVENT_RECOMMENDATIONS_UPDATED, events } from "../embed/events";
import { mapRecommendedToProduct } from "../lib/utils";

const SPA_BACKEND_KEY = "ww/spa/backendMode" as const;
type BackendMode = "dev" | "prod";

function getBackendMode(): BackendMode {
  try {
    const saved = localStorage.getItem(SPA_BACKEND_KEY);
    if (saved === "dev" || saved === "prod") return saved;
  } catch {}
  return "prod";
}

function getOrigins(mode: BackendMode): { origin: string; chatBase: string; initUrl: string } {
  const PROD = "https://wandway-backend.onrender.com";
  const DEV = "https://wandway-back.onrender.com";
  const origin = (mode === "prod" ? PROD : DEV).replace(/\/$/, "");
  return { origin, chatBase: `${origin}/chat`, initUrl: `${origin}/widget/init` };
}

type StoredRecommendation = {
  id: string | number;
  title: string;
  price: string;
  compareAtPrice?: string;
  currencyDisplay?: string;
  images: string[];
  productUrl?: string;
  // Variant fields (used by ProductCard to resolve variant-aware images)
  variantDimensions?: string[];
  variantCombinations?: Array<{
    values: string[];
    sku?: string;
    url?: string;
    price?: number;
    compare_at_price?: number;
    image_ids?: string[];
  }>;
  pickedVariantValues?: string[];
  pickedSku?: string;
  imageMeta?: Array<{ id: string; url: string }>;
};

function normalizeRecommendations(products: RecommendedProduct[] | null | undefined): StoredRecommendation[] {
  if (!Array.isArray(products)) return [];
  return products.map((rp) => {
    const p = mapRecommendedToProduct(rp);
    return {
      id: p.id,
      title: p.title,
      price: p.price || "",
      compareAtPrice: p.compareAtPrice,
      currencyDisplay: p.currencyDisplay,
      images: p.images,
      productUrl: p.productUrl,
      variantDimensions: p.variantDimensions,
      variantCombinations: p.variantCombinations,
      pickedVariantValues: p.pickedVariantValues,
      pickedSku: p.pickedSku,
      imageMeta: p.imageMeta,
    } satisfies StoredRecommendation;
  });
}

function storageKey(storeId: string) {
  return `ww:rec:${storeId}`;
}

export default function RecommendationsTest() {
  const { storeId } = useStore();
  const [sources, setSources] = React.useState<Array<{ productId: string; variantId: string }>>([
    { productId: "", variantId: "" },
  ]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lastCount, setLastCount] = React.useState<number | null>(null);

  const client = React.useMemo(() => {
    const refreshToken = async () => {
      try {
        const MODE = getBackendMode();
        const { initUrl, chatBase } = getOrigins(MODE);
        const url = `${initUrl}?store_id=${encodeURIComponent(storeId)}`;
        const urlWithSession = appendTestSessionId(url);
        const res = await fetch(urlWithSession, { method: "GET", credentials: "omit", cache: "no-store", mode: "cors" });
        if (!res.ok) return null;
        const data = await res.json();
        persistTestSessionId(data.test_session_id);
        persistTestConfig(data.test_config);
        updateGlobalTestConfig({ testSessionId: data.test_session_id, testConfig: data.test_config });
        const base = String(data?.api_base || "").replace(/\/$/, "");
        const secureBase = MODE === "prod" ? base.replace(/^http:\/\//i, "https://") : base;
        const fixedApiBase = /\/chat$/.test(secureBase) ? secureBase : `${secureBase}/chat`;
        const nextToken = data?.token ? String(data.token) : null;
        if (!nextToken) return null;
        return MODE === "prod" ? { token: nextToken, apiBase: fixedApiBase } : { token: nextToken, apiBase: chatBase };
      } catch {
        return null;
      }
    };

    const { chatBase } = getOrigins(getBackendMode());
    return createChatClient({ apiBase: chatBase, storeId, refreshToken });
  }, [storeId]);

  const emitUpdated = React.useCallback(() => {
    events.emit(EVENT_RECOMMENDATIONS_UPDATED);
  }, []);

  const clear = React.useCallback(() => {
    setError(null);
    setLastCount(null);
    try {
      localStorage.removeItem(storageKey(storeId));
    } catch {}
    emitUpdated();
  }, [emitUpdated, storeId]);

  const run = React.useCallback(async () => {
    const parsedSources = sources
      .map((source) => ({ productId: source.productId.trim(), variantId: source.variantId.trim() }))
      .filter((source) => source.productId.length > 0);
    if (parsedSources.length === 0) {
      setError("At least one product_id is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: any = { product_ids: parsedSources.map((source) => source.productId) };
      const selectedVariantIds: Record<string, string> = {};
      for (const source of parsedSources) {
        if (source.variantId) selectedVariantIds[source.productId] = source.variantId;
      }
      if (Object.keys(selectedVariantIds).length > 0) payload.selected_variant_ids = selectedVariantIds;

      const res: OnAddToCartResponse = await client.postOnAddToCart(payload);
      const recommended = (res as any)?.recommendation?.recommended_products as RecommendedProduct[] | undefined;
      const stored = normalizeRecommendations(recommended);

      try {
        localStorage.setItem(storageKey(storeId), JSON.stringify(stored));
      } catch {}
      emitUpdated();
      setLastCount(stored.length);
      if (stored.length === 0) {
        setError("No recommendations returned (or missing images).");
      }
    } catch (e) {
      setError((e as Error)?.message || "Request failed");
    } finally {
      setBusy(false);
    }
  }, [client, emitUpdated, sources, storeId]);

  const updateSource = React.useCallback((index: number, key: "productId" | "variantId", value: string) => {
    setSources((prev) => prev.map((source, i) => (i === index ? { ...source, [key]: value } : source)));
  }, []);

  const addSource = React.useCallback(() => {
    setSources((prev) => [...prev, { productId: "", variantId: "" }]);
  }, []);

  const removeSource = React.useCallback((index: number) => {
    setSources((prev) => {
      if (prev.length === 1) return [{ productId: "", variantId: "" }];
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  return (
    <div className="w-full min-h-screen p-6" style={{ backgroundColor: "var(--color-background)" }}>
      <div className="max-w-[1100px] mx-auto">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-[360px]">
            <div className="rounded-[12px] border p-4 bg-white">
              <div className="text-sm font-semibold mb-1">Recommendations test</div>
              <div className="text-[12px] text-gray-600 mb-4">
                Store: <span className="font-mono">{storeId}</span>
              </div>

              {sources.map((source, index) => (
                <div
                  key={`source-${index}`}
                  className={index === 0 ? "mt-0" : "mt-3 pt-3 border-t border-gray-100"}
                >
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700" htmlFor={`pid-${index}`}>
                      product_id {sources.length > 1 ? `#${index + 1}` : ""}
                    </label>
                    <button
                      type="button"
                      onClick={() => removeSource(index)}
                      disabled={busy}
                      className="text-[11px] text-gray-600 hover:text-gray-800 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    id={`pid-${index}`}
                    value={source.productId}
                    onChange={(e) => updateSource(index, "productId", e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded border bg-white font-mono"
                    placeholder="e.g. 123456789"
                    autoComplete="off"
                    inputMode="text"
                  />

                  <label className="block text-xs font-medium text-gray-700 mb-1 mt-3" htmlFor={`vid-${index}`}>
                    variant_sku (optional)
                  </label>
                  <input
                    id={`vid-${index}`}
                    value={source.variantId}
                    onChange={(e) => updateSource(index, "variantId", e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded border bg-white font-mono"
                    placeholder="SKU string"
                    autoComplete="off"
                    inputMode="text"
                  />
                </div>
              ))}

              <button
                type="button"
                onClick={addSource}
                disabled={busy}
                className="mt-3 px-3 py-2 text-sm rounded border bg-white disabled:opacity-50"
              >
                + Add source product
              </button>

              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={run}
                  disabled={busy}
                  className="px-3 py-2 text-sm rounded border bg-white disabled:opacity-50"
                >
                  {busy ? "Fetching…" : "Fetch"}
                </button>
                <button
                  type="button"
                  onClick={clear}
                  disabled={busy}
                  className="px-3 py-2 text-sm rounded border bg-white disabled:opacity-50"
                >
                  Clear
                </button>
              </div>

              {error && <div className="mt-3 text-[12px] text-red-700">{error}</div>}
              {!error && lastCount != null && (
                <div className="mt-3 text-[12px] text-gray-600">Stored {lastCount} recommendations.</div>
              )}
            </div>
          </div>

          <div className="flex-1">
            <div className="rounded-[12px] border p-4 bg-white">
              <RecommendationsWidget storeId={storeId} apiBase="" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


